import { HSpacer, MenuItem, Select, Text, VSpacer } from "@/components/DesignSystem";
import { getProgramPeriodLabel, sortPrograms } from '@/pages/RetailerOverview/helpers';
import { Category } from '@/themes/variant-interfaces/Category';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Stack, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ProgramPeriodSelectorProps {
  isAlreadySortedPrograms?: boolean,
  labelTextSize?: Category,
  onSelectProgram: (program: ApiRewardsProgram) => void,
  programs: ApiRewardsProgram[],
  selectedProgram?: ApiRewardsProgram,
  selectedValueTextSize?: Category,
  showAsDropdown?: boolean,
}
export const ProgramPeriodSelector = ({
  isAlreadySortedPrograms = false,
  labelTextSize = 'body-medium',
  onSelectProgram,
  programs,
  selectedProgram,
  selectedValueTextSize = 'title-small',
  showAsDropdown = false,
}: ProgramPeriodSelectorProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sortedPrograms: ApiRewardsProgram[] = isAlreadySortedPrograms
    ? programs
    : sortPrograms(programs);

  if (!programs.length) {
    return (
      <>
        <VSpacer size="4" />
        <VSpacer size="5" />
      </>
    );
  }

  if (showAsDropdown) {
    return (
      <Stack direction="column" spacing={1}>
        <Select
          displayEmpty
          onChangeValue={(programId) => {
            const program = programs.find((p) => p.id === programId);
            if (program) {
              onSelectProgram(program);
            }
          }}
          renderValue={() => selectedProgram ? (
            <Text category="body-large">
              {getProgramPeriodLabel(selectedProgram)}
            </Text>
          ) : (
            <Text category="body-large" sx={{ color: theme.palette.text.secondary }}>
              {t($ => $.overview.selectProgramPeriod)}
            </Text>
          )}
          sx={{ width: '100%' }}
          testID="program-period-select"
          value={selectedProgram?.id ?? ''}
          variant="outlined"
        >
          {sortedPrograms.map((program) => (
            <MenuItem key={program.id} testID={`${program.id}-menu-item`} value={program.id}>
              <Text category="body-large">
                {getProgramPeriodLabel(program)}
              </Text>
            </MenuItem>
          ))}
        </Select>
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <InfoOutlinedIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
            Points and variables will recalculate to show only available
            points for the selected year
          </Text>
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      <Stack alignItems="center" direction="row">
        <Text category={labelTextSize} sx={{ color: theme.palette.text.secondary }}>
          {t($ => $.overview.programPeriod)}
        </Text>
        <HSpacer size="2" />
        {programs.length === 1 && selectedProgram ? (
          <Text category={selectedValueTextSize}>
            {getProgramPeriodLabel(selectedProgram)}
          </Text>
        ) : (
          <Select
            displayEmpty
            onChangeValue={(programId) => {
              const program = programs.find((p) => p.id === programId);
              if (program) {
                onSelectProgram(program);
              }
            }}
            renderValue={() => selectedProgram ? (
              <Text category={selectedValueTextSize}>
                {getProgramPeriodLabel(selectedProgram)}
              </Text>
            ) : (
              <Text category={selectedValueTextSize}>
                {t($ => $.overview.selectProgramPeriod)}
              </Text>
            )}
            sx={{
              '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
              '& .MuiSelect-select': { minHeight: '100% !important' },
              height: '100%',
              minHeight: '100%',
              paddingX: 0,
              width: 'fit-content',
            }}
            testID="program-period-select"
            value={selectedProgram?.id ?? ''}
            variant="standard"
          >
            {sortedPrograms.map((program) => (
              <MenuItem key={program.id} testID={`${program.id}-menu-item`} value={program.id}>
                <Text category={selectedValueTextSize}>
                  {getProgramPeriodLabel(program)}
                </Text>
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>
    </>
  );
};
